import React, { useContext, useEffect, useState } from 'react';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import { Box, useTheme } from '@mui/material';
import { AlertMessage } from '@eposnow/ui-core';
import { STAFF_ROLES_CACHE_KEY } from '../../../constants';
import validationSchema from '../helpers/validationSchema';
import useCreateUpdate from '../../../helperHooks/useCreateUpdate';
import CreateEditCard from '../../../components/createEditComponents/CreateEditCard';
import { convertObjectToStringValues } from '../../../helpers/helpers';
import StaffRoleForm from './StaffRoleForm';
import { UserContext } from '../../../context/UserContext';
import { StaffRole, StaffRoleErrors } from '../../../types';
import { API_V1_HOSTNAME_KEY } from '../../../localStorageKeys';

const emptyObject: StaffRole = {
  id: null,
  name: '',
  description: '',
  globalTillAccess: false,
  adminOptsTillAdmin: false,
  adminOptsTillSettings: false,
  adminOptsBackOffice: false,
  adminOptsQuickAdd: true,
  adminOptsClockingSlip: false,
  adminOptsManagerOverride: false,
  adminOptsPettyCash: false,
  adminOptsFloatAdjust: false,
  adminOptsNoSale: false,
  adminOptsTillClosing: false,
  adminOptsTillClosing_BlindEOD: false,
  adminOptsTipOutSelf: false,
  adminOptsTipOutOthers: false,
  adminOptsCanWaiterBank: false,
  adminOptsOrderAllocationServer: false,
  adminOptsOrderAllocationTeamServer: false,
  adminOptsOrderAllocationTableManager: false,
  stockOptsStockSend: false,
  stockOptsStockReceive: false,
  stockOptsStockTake: false,
  stockOptsUnrestrictedStockTake: false,
  transactionOptsPayouts: false,
  transactionOptsVoidLine: false,
  transactionOptsVoidUnordered: false,
  transactionOptsClear: false,
  transactionOptsTablePlanning: false,
  transactionOptsRemoveFromTable: false,
  transactionOptsDisableServiceCharge: false,
  transactionOptsHold: false,
  transactionOptsChangeTabSpendLimit: false,
  transactionOptsCreateMiscProducts: false,
  discountOptsItemDiscount: false,
  discountOptsItemDiscountLimit: null,
  discountOptsItemDiscountLimitPct: null,
  discountOptsBasketDiscount: false,
  discountOptsBasketDiscountLimit: null,
  discountOptsBasketDiscountLimitPct: null,
  discountOptsRefunds: false,
  discountOptsRefundLimit: null,
  customerOptsCreditLimit: null,
  adminOptsCustomerList: false,
  customerOptsCustomerSelect: false,
  customerOptsEditCustomerPoints: false,
};

const CreateEditStaffRoleCard = ({
  currentObject,
  resetObject,
  setSuccessMessage,
  errorMessage,
  setErrorMessage,
  errors,
  setErrors,
  mode,
}: {
  currentObject: StaffRole;
  resetObject: () => void;
  setSuccessMessage: (message: string) => void;
  errorMessage: string;
  setErrorMessage: (message: string) => void;
  errors: StaffRoleErrors;
  setErrors: (obj: any) => void;
  mode: string;
}) => {
  const [initialObject, setInitialObject] = useState<StaffRole>(emptyObject);
  const [objectId, setObjectId] = useState<string>('0');
  const { t } = useTranslation();
  const theme = useTheme();
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
  const { locale } = useContext(UserContext);

  const removeSavedObject = () => {
    const savedObjects = JSON.parse(sessionStorage.getItem(STAFF_ROLES_CACHE_KEY) || '{}');
    delete savedObjects[`${objectId}`];
    sessionStorage.setItem(STAFF_ROLES_CACHE_KEY, JSON.stringify(savedObjects));
  };

  const { isLoading, apiCall } = useCreateUpdate(
    mode,
    'staffRoles',
    `${localStorage.getItem(API_V1_HOSTNAME_KEY)}onboarding/proxy/staff-roles`,
    'staff roles',
    STAFF_ROLES_CACHE_KEY,
    resetObject,
    removeSavedObject,
    setSuccessMessage,
    setErrorMessage,
    setErrors,
    mode === 'create' ? '0' : currentObject.id?.toString()
  );

  useEffect(() => {
    const savedObjects = JSON.parse(sessionStorage.getItem(STAFF_ROLES_CACHE_KEY) || '{}');
    const newObjectId = mode === 'create' ? '0' : currentObject.id?.toString();
    setObjectId(newObjectId);
    const savedObject = savedObjects[`${newObjectId}`];

    let newInitialObject: StaffRole = { ...emptyObject, id: 0, ...savedObject };
    if (mode === 'create')
      sessionStorage.setItem(
        STAFF_ROLES_CACHE_KEY,
        JSON.stringify({
          ...savedObjects,
          [`${newObjectId}`]: newInitialObject,
        })
      );
    else {
      const parsedCurrentObject = { ...currentObject };
      newInitialObject = {
        ...parsedCurrentObject,
        id: parsedCurrentObject.id,
        name: parsedCurrentObject.name,
        description: parsedCurrentObject.description,
      };
    }
    setInitialObject(newInitialObject);
  }, [currentObject]);

  const formik = useFormik({
    initialValues: initialObject,
    validationSchema,
    enableReinitialize: true,
    onSubmit: (values, actions) => {
      if (isLoading) return;

      actions.setSubmitting(false);
      const requestValues = validationSchema.cast(values);
      if (mode === 'create') {
        apiCall(requestValues);
      } else if (
        JSON.stringify(convertObjectToStringValues(requestValues)) !==
        JSON.stringify(convertObjectToStringValues(initialObject))
      ) {
        apiCall({ oldObject: initialObject, newObject: requestValues });
      }
    },
  });

  const clearError = (errorKey: string) => {
    setErrors(({ [errorKey as keyof StaffRoleErrors]: _value, ...remaining }) => remaining);
  };

  useEffect(() => {
    if (formik.values?.id == null) return;

    const savedObject = mode === 'create' ? { ...emptyObject, id: 0 } : { ...currentObject };

    setHasUnsavedChanges(
      JSON.stringify(convertObjectToStringValues(formik.values)) !==
        JSON.stringify(convertObjectToStringValues(savedObject))
    );
  }, [JSON.stringify(formik.values)]);

  return (
    <CreateEditCard
      resetObject={resetObject}
      errorMessage={errorMessage}
      setErrorMessage={setErrorMessage}
      mode={mode}
      translationKey="staffRoles"
    >
      <Box padding={2}>
        <StaffRoleForm
          formik={formik}
          cancelAction={() => {
            resetObject();
            removeSavedObject();
          }}
          objectId={objectId}
          isSubmitting={isLoading}
          errors={errors}
          clearError={clearError}
        />
        {hasUnsavedChanges && (
          <AlertMessage type="warning" locale={locale} theme={theme}>
            {t('errors.unsavedChanges')}
          </AlertMessage>
        )}
      </Box>
    </CreateEditCard>
  );
};

export default CreateEditStaffRoleCard;
